import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchSharedSummary } from '../services/api';
import { getOrderStatusLabel, getStatusCodeDescription, getTemperatureClass } from '../utils/orderDisplay';
import MapComponent from './MapComponent';
import OrderStatusGrid from './OrderStatusGrid';
import OrderTimeline from './OrderTimeline';
import SharedIntro from './SharedIntro';
import '../styles/global.css';
import './SharedLinkPage.css';

/** Phone viewport ≤768 — matches LiveLink shell tabs */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  return isMobile;
}

/** Full intro on every load (each one re-fetches the API). Reduced motion gets the short fade. */
function pickIntroMode() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'short' : 'full';
}

/**
 * SharedLinkPage - Public page for viewing order status via shared link
 * 
 * Features:
 * - Displays order details, progress, and timeline
 * - Shows interactive map with pickup/delivery locations and route
 * - Responsive design with mobile tabs
 * - Real-time temperature monitoring
 * - ETA calculations
 */
function SharedLinkPage() {
  const { token } = useParams();
  const isMobile = useIsMobile();

  // =============================================================================
  // STATE MANAGEMENT
  // =============================================================================
  
  const [orderDetails, setOrderDetails] = useState(null);
  const [statusUpdates, setStatusUpdates] = useState([]);
  const [coordinates, setCoordinates] = useState({ pickup: null, delivery: null });
  const [routeCoordinates, setRouteCoordinates] = useState([]);
  const [routePhase, setRoutePhase] = useState(null);
  const [estimatedArrival, setEstimatedArrival] = useState(null);
  const [distanceRemaining, setDistanceRemaining] = useState(null);
  
  // Loading states
  const [loadingOrder, setLoadingOrder] = useState(true);
  const [loadingMap, setLoadingMap] = useState(false);
  const [loadingEta, setLoadingEta] = useState(false);
  const [error, setError] = useState('');
  
  // UI state
  const [activeTab, setActiveTab] = useState('details');
  const [introMode] = useState(() => pickIntroMode());
  const [revealed, setRevealed] = useState(false);
  const [introDone, setIntroDone] = useState(false);
  const josephLogoRef = useRef(null);
  const separatorRef = useRef(null);
  const partnerLogoRef = useRef(null);
  const productLogoRef = useRef(null);

  const finishIntro = () => {
    setIntroDone(true);
    document.documentElement.classList.remove('shared-boot');
  };

  useEffect(() => () => {
    document.documentElement.classList.remove('shared-boot');
  }, []);

  // =============================================================================
  // UTILITY FUNCTIONS
  // =============================================================================

  /**
   * Format date/time for display
   */
  const formatDateTime = (date) => {
    if (!date) return '';
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // =============================================================================
  // DATA FETCHING
  // =============================================================================

  /**
   * Fetch all order data in one consolidated call
   */
  useEffect(() => {
    const loadOrderData = async () => {
      setLoadingOrder(true);
      setLoadingMap(true);
      setLoadingEta(true);
      setError('');
      
      // Reset state
      setOrderDetails(null);
      setStatusUpdates([]);
      setCoordinates({ pickup: null, delivery: null });
      setRouteCoordinates([]);
      setRoutePhase(null);
      setEstimatedArrival(null);
      setDistanceRemaining(null);

      try {
        const data = await fetchSharedSummary(token);
        const { order, coordinates: coords, eta, updates, route, routeWindow } = data;

        // Set order details and status history
        setOrderDetails(order);
        // ENRTE is an internal code. Shared customers never see that row.
        const history = (updates || []).filter((u) => String(u?.statuscode || '').toUpperCase() !== 'ENRTE');
        setStatusUpdates(history.length ? history : [order]);

        // Set map coordinates
        if (coords) {
          const pickup = coords.pickup ? [coords.pickup.lat, coords.pickup.lon] : null;
          const delivery = coords.delivery ? [coords.delivery.lat, coords.delivery.lon] : null;
          setCoordinates({ pickup, delivery });
        }

        setRoutePhase(routeWindow?.phase || null);
        // Set route data (to_pickup may be a single GPS point)
        setRouteCoordinates(Array.isArray(route) ? route : []);

        // Set ETA
        if (eta && eta.status === 'complete') {
          setEstimatedArrival('Complete');
          setDistanceRemaining(0);
        } else if (eta && eta.arrival_iso) {
          const arrivalTime = new Date(eta.arrival_iso);
          if (!isNaN(arrivalTime)) {
          setEstimatedArrival(formatDateTime(arrivalTime));
          } else {
            setEstimatedArrival('Not available');
          }
          if (eta.distance_miles !== undefined) {
            setDistanceRemaining((eta.distance_miles * 1.60934).toFixed(2));
          } else if (eta.distance_km !== undefined) {
            setDistanceRemaining(eta.distance_km);
          }
        } else {
          setEstimatedArrival('Not available');
          if (eta && eta.distance_miles !== undefined) {
            setDistanceRemaining((eta.distance_miles * 1.60934).toFixed(2));
          } else if (eta && eta.distance_km !== undefined) {
            setDistanceRemaining(eta.distance_km);
          }
        }
      } catch (error) {
        setError('Failed to fetch order details. Please try again.');
      } finally {
        setLoadingOrder(false);
        setLoadingMap(false);
        setLoadingEta(false);
      }
    };

    loadOrderData();
  }, [token]);

  // =============================================================================
  // STATUS & PROGRESS LOGIC
  // =============================================================================

  /**
   * Get the most recent status update
   */
  const getLatestStatus = () => {
    if (!statusUpdates || statusUpdates.length === 0) return null;

    const statusOrder = {
      'DISP': 1, 'ARRSHIP': 2, 'DEPSHIP': 3, 'SPOTTED': 4, 'SPTLD': 5,
      'PICKD': 6, 'ARRCONS': 7, 'DEPCONS': 8, 'DELVD': 9, 'COMPLETE': 10, 'COMPLETED': 10
    };

    const sortedUpdates = [...statusUpdates].sort((a, b) => {
      const dateA = new Date(a.status_updated || a.UpdatedOn);
      const dateB = new Date(b.status_updated || b.UpdatedOn);
      const timeComparison = dateB.getTime() - dateA.getTime();
      
      if (timeComparison === 0) {
        const orderA = statusOrder[a.statuscode] || 0;
        const orderB = statusOrder[b.statuscode] || 0;
        return orderB - orderA;
      }
      
      return timeComparison;
    });
    
    return sortedUpdates[0];
  };

  /**
   * Calculate delivery progress percentage
   */
  const getProgressPercentage = () => {
    const progressSequence = {
      'CANCL': 0, 'DISP': 15, 'ARRSHIP': 30, 'DEPSHIP': 45, 'SPOTTED': 60,
      'SPTLD': 60, 'PICKD': 65, 'ARRCONS': 85, 'DEPCONS': 100, 'DELVD': 100,
      'COMPLETE': 100, 'COMPLETED': 100
    };
    
    const latestStatus = getLatestStatus();
    if (latestStatus && progressSequence[latestStatus.statuscode] !== undefined) {
      return progressSequence[latestStatus.statuscode];
    }
    return 0;
  };

  /**
   * Get current location description based on latest status
   */
  const getCurrentLocation = () => {
    if (orderDetails?.current_location) {
      return orderDetails.current_location;
    }
    const latestStatus = getLatestStatus();
    if (!latestStatus) return 'Unknown';

    const status = latestStatus.statuscode;
    
    switch (status) {
      case 'CANCL': return 'Order Cancelled';
      case 'DISP': return 'En route to pickup';
      case 'PICKD': return 'Picked up, en route';
      case 'ARRSHIP': return orderDetails?.origin_address || 'At pickup location';
      case 'DEPSHIP': return 'Departed from pickup location';
      case 'SPOTTED':
      case 'SPTLD': return 'At Spot loading/unloading area';
      case 'ARRCONS': return orderDetails?.destination_address || 'At delivery location';
      case 'DEPCONS': return 'Departed from delivery location';
      case 'DELVD':
      case 'COMPLETE':
      case 'COMPLETED': return orderDetails?.destination_address || 'Delivery completed';
      default: return 'In transit';
    }
  };

  /**
   * Check if delivery is at or past consignee
   */
  const isAtOrPastConsignee = (status) => {
    const completedStatuses = ['ARRCONS', 'DEPCONS', 'DELVD', 'COMPLETE', 'COMPLETED'];
    return completedStatuses.includes(status);
  };

  /**
   * Get remaining distance
   */
  const getDistanceRemaining = () => {
    const latestStatus = getLatestStatus();
    if (!latestStatus) return 'Not available';
    
    if (isAtOrPastConsignee(latestStatus.statuscode)) {
      return '0';
    }
    
    if (distanceRemaining !== null && distanceRemaining !== undefined) {
      return distanceRemaining;
    }
    
    return orderDetails?.distance_remaining || 'Calculating...';
  };

  /** Map status code to colored badge classes. */
  const getStatusBadgeClass = (statuscode, compact = false) => {
    const base = compact ? 'status-badge-compact' : 'status-badge';
    if (!statuscode) return base;
    const s = String(statuscode).toUpperCase();
    let mod = 'status-badge--transit';
    if (s === 'CANCL') mod = 'status-badge--cancelled';
    else if (['DELVD', 'COMPLETE', 'COMPLETED', 'DEPCONS'].includes(s)) mod = 'status-badge--complete';
    else if (['ASSGN', 'DISP', 'ENTRY', 'AVAIL'].includes(s)) mod = 'status-badge--assigned';
    return `${base} ${mod}`;
  };

  // =============================================================================
  // DERIVED DATA
  // =============================================================================

  // Map orderDetails to format expected by MapComponent
  const mappedSelectedOrder = orderDetails ? {
    VehicleNum: orderDetails.vehiclenum,
    'Pickup Date': orderDetails.pickup_date,
    'Delivery Date': orderDetails.delivery_date,
    order_id: orderDetails.order_id,
    // Ensure Status matches main portal casing for icon logic
    Status: orderDetails.status ? String(orderDetails.status).toUpperCase() : undefined,
    ...orderDetails
  } : null;

  const latestStatus = getLatestStatus();
  const eventLabel = loadingOrder
    ? '…'
    : (latestStatus ? getStatusCodeDescription(latestStatus.statuscode) : 'Unknown');
  const etaDisplay = loadingEta ? '…' : (estimatedArrival || '—');
  const distanceDisplay = loadingOrder || loadingEta ? '…' : `${getDistanceRemaining()} km`;
  const events = statusUpdates.map((u) => ({
    status: u.statuscode,
    UpdatedOn: u.status_updated,
    comments: u.comments || '',
  }));
  const timelineProps = {
    events,
    loading: loadingOrder,
    originAddress: orderDetails?.origin_address,
    destinationAddress: orderDetails?.destination_address,
    shipperTemp: orderDetails?.shipper_temp,
    consigneeTemp: orderDetails?.consignee_temp,
    haulTempMin: orderDetails?.haul_temp_f_min,
    haulTempMax: orderDetails?.haul_temp_f_max,
    itemClass: 'reveal',
  };

  const sharedStatusGrid = (
    <OrderStatusGrid
      className="reveal"
      style={{ '--i': 3 }}
      orderId={loadingOrder ? '…' : (orderDetails?.order_id?.trim() || '—')}
      status={loadingOrder ? '…' : (
        // Header code ENRTE stays internal. Show the latest customer event.
        String(orderDetails?.status || '').toUpperCase() === 'ENRTE'
          ? eventLabel
          : getOrderStatusLabel(orderDetails?.status)
      )}
      eta={etaDisplay}
      distance={distanceDisplay}
      truck={loadingOrder ? '…' : (orderDetails?.vehiclenum || '—')}
      trailer={loadingOrder ? '…' : (orderDetails?.trailer || '—')}
    />
  );

  const sharedTravelHistory = (
    <OrderTimeline {...timelineProps} />
  );

  // =============================================================================
  // RENDER
  // =============================================================================

  const intro = !introDone && (
    <SharedIntro
      mode={introMode}
      companyUrl={orderDetails?.company_url}
      dataReady={!loadingOrder}
      getTargets={() => ({
        logo: josephLogoRef.current,
        shake: separatorRef.current,
        partner: partnerLogoRef.current,
        product: productLogoRef.current,
      })}
      onReveal={() => setRevealed(true)}
      onDone={finishIntro}
    />
  );

  return (
    <>
    {intro}
    <div className={`shared-page-container${revealed ? ' is-revealed' : ''}`}>
      {/* Header */}
      <header className="shared-header">
        <div className={`header-left${introMode === 'full' && !introDone ? ' header-left--intro' : ''}`}>
          <img
            ref={josephLogoRef}
            src={`${process.env.PUBLIC_URL || ''}/jtg-logo.png`}
            alt="Joseph Transportation Group"
            className="joseph-logo"
          />
          {orderDetails?.company_url && (
            <>
              <span ref={separatorRef} className="separator fade-in">
                <i className="fas fa-handshake"></i>
              </span>
              <img
                ref={partnerLogoRef}
                src={orderDetails.company_url}
                alt="Partner Logo"
                className="partner-logo fade-in"
              />
            </>
          )}
        </div>
        <div className="header-right">
          <img
            ref={productLogoRef}
            src={`${process.env.PUBLIC_URL || ''}/livelink-mark.png`}
            alt="LiveLink"
            className={`header-product${introMode === 'full' && !introDone ? ' header-product--intro' : ''}`}
          />
        </div>
      </header>

      {error ? (
        <div className="error-message reveal" style={{ '--i': 0 }}>{error}</div>
      ) : (
      <>
      {/* Mobile Tab Navigation */}
      {isMobile && (
        <div className="mobile-tabs reveal" style={{ '--i': 0 }}>
          <button 
            className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            Details
          </button>
          <button 
            className={`tab-button ${activeTab === 'map' ? 'active' : ''}`}
            onClick={() => setActiveTab('map')}
          >
            Tracking
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className={`shared-content${isMobile ? ' is-mobile' : ''}`}>
        {/* Mobile Details View */}
        {isMobile && activeTab === 'details' && (
          <div className="mobile-details-container">
            <div className="compact-order-info reveal" style={{ '--i': 0 }}>
              <div className="order-id">Order #{loadingOrder ? '…' : (orderDetails?.order_id?.trim() || '—')}</div>
              <div className="vehicle-info">
                <span>Truck: {loadingOrder ? '…' : (orderDetails?.vehiclenum || '—')}</span>
                <span>Trailer: {loadingOrder ? '…' : (orderDetails?.trailer || '—')}</span>
              </div>
            </div>

            <div className="compact-progress reveal" style={{ '--i': 1 }}>
              <div className="progress-info">
                <span className="progress-label">Progress</span>
                <span className={loadingOrder ? 'status-badge-compact' : getStatusBadgeClass(latestStatus?.statuscode, true)}>
                  {eventLabel}
                </span>
              </div>
              <div className="progress-bar-compact">
                <div className="progress-fill" style={{ width: `${loadingOrder ? 0 : getProgressPercentage()}%` }}></div>
              </div>
            </div>

            <div className="location-grid reveal" style={{ '--i': 2 }}>
              <div className="location-item">
                <div className="location-label"><i className="fas fa-map-marker-alt loc-muted"></i> Pickup</div>
                <div className="location-text">{loadingOrder ? '…' : (orderDetails?.origin_address || '—')}</div>
              </div>
              <div className="location-item">
                <div className="location-label"><i className="fas fa-map-marker-alt loc-muted"></i> Dropoff</div>
                <div className="location-text">{loadingOrder ? '…' : (orderDetails?.destination_address || '—')}</div>
              </div>
              <div className="location-item">
                <div className="location-label"><i className="fas fa-location-arrow loc-muted"></i> Current</div>
                <div className="location-text">{loadingOrder ? '…' : getCurrentLocation()}</div>
              </div>
              <div className="location-item">
                <div className="location-label">Distance</div>
                <div className="location-text">{distanceDisplay}</div>
              </div>
            </div>

            <div className="eta-temp-compact reveal" style={{ '--i': 3 }}>
              <div className="eta-compact">
                <div className="compact-label">ETA</div>
                <div className="compact-value">{etaDisplay}</div>
              </div>
            </div>

            {sharedTravelHistory}
          </div>
        )}

        {/* Mobile Tracking: short map + details + history (portal pattern) */}
        {isMobile && activeTab === 'map' && (
          <div className="mobile-map-container mobile-map-container--tracking">
            <div className="shared-tracking-map reveal" style={{ '--i': 0 }}>
              {loadingMap ? (
                <div className="map-loading-state">
                  <div className="loading-spinner">
                    <span>Loading map...</span>
                  </div>
                </div>
              ) : (coordinates.pickup || coordinates.delivery ? (
                <MapComponent
                  originAddress={orderDetails?.origin_address}
                  destinationAddress={orderDetails?.destination_address}
                  pickupCoords={coordinates.pickup}
                  deliveryCoords={coordinates.delivery}
                  routeCoords={routeCoordinates}
                  routePhase={routePhase}
                  selectedOrder={mappedSelectedOrder}
                  disableLiveRouteFetch
                  currentLocationLabel={orderDetails?.current_location}
                  fillParent
                />
              ) : (
                <div className="map-loading-state">
                  <div className="loading-spinner">
                    <span>Unable to load map coordinates</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="shared-tracking-below">
              {sharedStatusGrid}
              <OrderTimeline className="timeline-scroll" {...timelineProps} />
            </div>
          </div>
        )}

        {/* Desktop Layout (unchanged) */}
        {!isMobile && (
          <>
            {/* Delivery Details Section */}
            <div className="delivery-details delivery-details--grow">
              <h2 className="reveal" style={{ '--i': 0 }}>Delivery Details</h2>
              {/* Shipment Info */}
              <div className="shipment-info reveal" style={{ '--i': 1 }}>
                <div className="order-header-row">
                  <div className="order-details-center">
                    <div className="shipment-header">Order ID # {loadingOrder ? <span>Loading...</span> : (orderDetails?.order_id?.trim() || 'Unknown')}</div>
                  </div>
                  <div className="driver-info-block">
                    <p>Truck Number: {loadingOrder ? <span>Loading...</span> : (orderDetails?.vehiclenum || 'Not assigned')}</p>
                    <p>Trailer Number: {loadingOrder ? <span>Loading...</span> : (orderDetails?.trailer || 'Not assigned')}</p>
                  </div>
                </div>
              </div>
              {/* Progress Section */}
              <div className="progress-section reveal" style={{ '--i': 2 }}>
                <div className="progress-label">
                  <span>Progress</span>
                  <span className={loadingOrder ? 'status-badge' : getStatusBadgeClass(getLatestStatus()?.statuscode)}>
                    {loadingOrder ? <span>Loading...</span> : (getLatestStatus() ? getStatusCodeDescription(getLatestStatus().statuscode) : 'Unknown')}
                  </span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${loadingOrder ? 0 : getProgressPercentage()}%` }}></div>
                </div>
              </div>
              {/* Location Details */}
              <div className="location-details reveal" style={{ '--i': 3 }}>
                <div className="detail-row">
                  <div className="detail-column">
                    <h3>Pickup Location</h3>
                    <p><i className="fas fa-map-marker-alt loc-muted"></i> {loadingOrder ? <span>Loading...</span> : (orderDetails?.origin_address || '—')}</p>
                  </div>
                  <div className="detail-column">
                    <h3>Dropoff Location</h3>
                    <p><i className="fas fa-map-marker-alt loc-muted"></i> {loadingOrder ? <span>Loading...</span> : (orderDetails?.destination_address || '—')}</p>
                  </div>
                </div>
                <div className="detail-row">
                  <div className="detail-column">
                    <h3>Current Location</h3>
                    <p><i className="fas fa-location-arrow loc-muted"></i> {loadingOrder ? <span>Loading...</span> : getCurrentLocation()}</p>
                  </div>
                  <div className="detail-column">
                    <h3>Distance Remaining</h3>
                    <p>{loadingOrder ? <span>Loading...</span> : getDistanceRemaining()} km</p>
                  </div>
                </div>
              </div>
              {/* ETA and Temperature Section Side by Side */}
              <div className="eta-temp-row reveal" style={{ '--i': 4 }}>
                <div className="eta-section">
                  <h3>Estimated Time of Arrival</h3>
                  <div className="eta-time">
                    {loadingEta ? (
                      <span className="eta-calculating">Calculating...</span>
                    ) : (
                      estimatedArrival || 'Not available'
                    )}
                  </div>
                </div>
                {(() => {
                  const cRaw = orderDetails?.consignee_temp ? String(orderDetails.consignee_temp).trim() : '';
                  const sRaw = orderDetails?.shipper_temp ? String(orderDetails.shipper_temp).trim() : '';
                  const tempVal = cRaw || sRaw;
                  if (!tempVal) return null;
                  return (
                    <div className="temperature-section">
                      <h3><i className="fas fa-thermometer-half loc-current"></i> Haul Temperature</h3>
                      <div className={`temperature-value ${getTemperatureClass(tempVal)}`}>
                        @{'\u00A0'}{tempVal}{!isNaN(Number(tempVal)) ? '° F' : ''}
                      </div>
                    </div>
                  );
                })()}
              </div>
              {/* Movement Timeline */}
              <div className="movement-timeline">
                <OrderTimeline {...timelineProps} />
              </div>
            </div>
            {/* Map Section - Desktop */}
            <div className="shared-map-container shared-map-container--column reveal" style={{ '--i': 1 }}>
              <div className="integrated-map-container integrated-map-container--fill">
                {loadingMap ? (
                  <div className="map-loading-state">
                    <div className="loading-spinner">
                      <span>Loading map...</span>
                    </div>
                  </div>
                ) : (coordinates.pickup || coordinates.delivery ? (
                  <MapComponent
                    originAddress={orderDetails?.origin_address}
                    destinationAddress={orderDetails?.destination_address}
                    pickupCoords={coordinates.pickup}
                    deliveryCoords={coordinates.delivery}
                    routeCoords={routeCoordinates}
                    routePhase={routePhase}
                    selectedOrder={mappedSelectedOrder}
                    disableLiveRouteFetch
                    currentLocationLabel={orderDetails?.current_location}
                  />
                ) : (
                  <div className="map-loading-state">
                    <div className="loading-spinner">
                      <span>Unable to load map coordinates</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
      </>
      )}
    </div>
    </>
  );
}

export default SharedLinkPage;